import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAppSessionScope } from "~/lib/app-session-scope";
import {
  getCashClassificationQueryOptions,
  type CashAssetClassification,
  type CashClassificationClient,
} from "./cash-classification-query-options";

/** Catalog identity only. Custody collections continue to own amounts and value. */
export function useCashAssetClassification(input: {
  readonly assetIds: readonly string[];
  readonly enabled: boolean;
  readonly platformApi?: CashClassificationClient;
}) {
  const session = useAppSessionScope();
  const query = useQuery({
    ...getCashClassificationQueryOptions({
      ...input,
      sessionScopeToken: session.scopeToken,
    }),
    enabled: input.enabled && input.assetIds.length > 0,
  });
  const data = input.enabled ? query.data : undefined;
  const classificationByAssetId = useMemo<
    ReadonlyMap<string, CashAssetClassification>
  >(
    () =>
      new Map(data?.items.map((item) => [item.assetId, item.classification])),
    [data],
  );
  const isReady =
    input.enabled && (input.assetIds.length === 0 || data !== undefined);
  return {
    classificationByAssetId,
    isReady,
    isLoading: input.enabled && !isReady && !query.error,
    error: input.enabled ? query.error : null,
    refetch: query.refetch,
  };
}
